import type { ConditionalRequirements } from "#bb/lib/conditionalRules";
import {
  convertUserStateValue,
  getUserMetricValue,
  userMetricPath,
  type UserMetricRecord,
} from "#bb/lib/userState";

export function useConditionalUserData(resourceKey: MaybeRefOrGetter<string>) {
  const { siteId } = useSite();
  const { profile, getProfile } = useProfile();
  const logger = useLogger("ConditionalUserData");
  const conditionValues = useConditionValues(resourceKey);
  const userStateStorage = useUserStateStorage();

  async function downloadRecord<T>(path: string): Promise<T | null> {
    const resolvedSiteId = siteId.value;
    if (!resolvedSiteId) {
      throw new Error("A site ID is required to load conditional user data.");
    }

    const { getEntry } = useFileEntries(siteId);
    const entry = await getEntry(path, "metadata", {
      suppressNotFoundToast: true,
    });
    if (!entry?.path || !entry.versions?.length) return null;

    const latestVersion = entry.versions.reduce((latest, version) =>
      version.version > latest.version ? version : latest,
    );
    try {
      const raw = await useApiFetch<string>(
        `/v1/file/${resolvedSiteId}/download/${entry.path}?version=${latestVersion.id}`,
        { responseType: "text" },
      );
      return JSON.parse(raw) as T;
    } catch (error) {
      logger.error("Unable to load conditional user data", {
        path,
        error,
      });
      return null;
    }
  }

  async function load(requirements: ConditionalRequirements) {
    if (
      !requirements.userStates.length
      && !requirements.userMetrics.length
    ) {
      return;
    }
    const currentProfile = profile.value ?? await getProfile();
    const userId = currentProfile?.id;
    if (!userId) {
      throw new Error("A user profile is required to load conditional user data.");
    }

    await Promise.all([
      ...requirements.userStates.map(async (source) => {
        try {
          const record = await userStateStorage.load({
            dataPath: source.dataPath,
            key: source.key,
          });
          const conversion = convertUserStateValue(
            record,
            source.valueType,
          );
          const value = conversion.ok ? conversion.value : undefined;
          if (record && !conversion.ok) {
            logger.warn("User state value could not be converted for its conditional rule", {
              dataPath: source.dataPath,
              key: source.key,
              expectedType: source.valueType,
              actualType: record.valueType,
              reason: conversion.reason,
            });
          }
          conditionValues.setValue(source, value);
        } catch (error) {
          conditionValues.setValue(source, undefined);
          logger.error("Unable to load user state condition value", {
            dataPath: source.dataPath,
            key: source.key,
            error,
          });
        }
      }),
      ...requirements.userMetrics.map(async (source) => {
        const record = await downloadRecord<UserMetricRecord>(
          userMetricPath(userId, source.dataPath, source.key),
        );
        conditionValues.setValue(source, getUserMetricValue(record));
      }),
    ]);
  }

  return { load };
}
